// src/pages/admin/sandbox/DbConnectionsTab.tsx
// Direct-connection probe for external databases. Today: Practice Engine
// (Engine_BMSS on eastussqleclp.database.windows.net). We currently run all
// PE queries through a Logic App (PORTAL_DATA_URL_BASE64); this tab is the
// first surface to skip the Logic App and talk to the PE DB directly via
// the mssql driver on the Functions side, using PE_SQL_* env vars.
import React, { useState } from 'react';
import { Database, PlayCircle, AlertTriangle, CheckCircle2, Copy } from 'lucide-react';
import { runPeDbTest, type PeDbTestResponse } from '../../../services/peDbTest';

const DEFAULT_SQL = 'SELECT TOP 20 * FROM dbo.tblTranWIP FOR JSON AUTO, INCLUDE_NULL_VALUES';

const PRESETS: { label: string; sql: string }[] = [
  { label: 'Top 20 tblTranWIP (JSON)', sql: DEFAULT_SQL },
  { label: 'Top 20 tblTranWIP (rows)', sql: 'SELECT TOP 20 * FROM dbo.tblTranWIP' },
  { label: 'Server version',           sql: 'SELECT @@VERSION AS version' },
  { label: 'List user tables',         sql: "SELECT TOP 50 TABLE_SCHEMA, TABLE_NAME FROM INFORMATION_SCHEMA.TABLES WHERE TABLE_TYPE='BASE TABLE' ORDER BY TABLE_SCHEMA, TABLE_NAME" },
];

export default function DbConnectionsTab() {
  const [sqlText, setSqlText] = useState<string>(DEFAULT_SQL);
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState<PeDbTestResponse | null>(null);

  async function run() {
    setRunning(true);
    setResult(null);
    try {
      const res = await runPeDbTest(sqlText);
      setResult(res);
    } catch (e: any) {
      setResult({
        ok: false,
        elapsedMs: 0,
        server: '', database: '', user: '',
        query: sqlText,
        error: e?.message || String(e),
      });
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Database size={18} /> Practice Engine — direct DB probe
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                Opens an mssql/tedious connection to <code>Engine_BMSS</code> on the PE SQL server using
                {' '}<code>PE_SQL_*</code> env vars. Same credentials our Python jobs use with ODBC Driver 17.
                Read-only: only <code>SELECT</code>/<code>WITH</code> statements are accepted by the backend.
              </div>
            </div>
          </div>
        </div>
        <div className="bd">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {PRESETS.map(p => (
              <button
                key={p.label}
                className="btn"
                onClick={() => setSqlText(p.sql)}
                style={{ fontSize: 12 }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <textarea
            value={sqlText}
            onChange={e => setSqlText(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%',
              minHeight: 110,
              fontFamily: 'Consolas, "Cascadia Mono", ui-monospace, monospace',
              fontSize: 13,
              padding: 10,
              borderRadius: 8,
              border: '1px solid #27303b',
              background: '#0b0d10',
              color: '#e9eef5',
              resize: 'vertical',
            }}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <button className="btn primary" onClick={run} disabled={running}>
              <PlayCircle size={14} /> {running ? 'Running…' : 'Run query'}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              POST /api/pe-db-test — admin only.
            </span>
          </div>
        </div>
      </section>

      {result && <ResultPanel result={result} />}
    </>
  );
}

function ResultPanel({ result }: { result: PeDbTestResponse }) {
  const [showRaw, setShowRaw] = useState(false);
  const ok = !!result.ok;

  return (
    <section className="card">
      <div className="hd">
        <div className="row">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 760 }}>
            {ok ? <CheckCircle2 size={18} color="#22c55e" /> : <AlertTriangle size={18} color="#f59e0b" />}
            {ok ? 'Connection succeeded' : 'Connection failed'}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {result.elapsedMs} ms
            {typeof result.rowCount === 'number' ? ` · ${result.rowCount} row${result.rowCount === 1 ? '' : 's'}` : ''}
          </div>
        </div>
      </div>
      <div className="bd">
        <ConnectionMeta result={result} />

        {!ok && result.error && (
          <>
            <pre style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.35)',
              color: '#fecaca',
              padding: 12, borderRadius: 8, marginTop: 12,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontSize: 12,
            }}>
              {result.code ? `[${result.code}] ` : ''}{result.error}
            </pre>
            {(result.sqlNumber || result.sqlState || result.sqlServerName) && (
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 4, fontSize: 12, marginTop: 8 }}>
                {result.sqlNumber != null && (<><div className="muted">SQL Number</div><div>{result.sqlNumber} <SqlNumberHint n={result.sqlNumber} /></div></>)}
                {result.sqlState  != null && (<><div className="muted">SQL State</div><div>{result.sqlState} <SqlStateHint number={result.sqlNumber} state={result.sqlState} /></div></>)}
                {result.sqlClass  != null && (<><div className="muted">SQL Class</div><div>{result.sqlClass}</div></>)}
                {result.sqlLineNumber != null && (<><div className="muted">Line</div><div>{result.sqlLineNumber}</div></>)}
                {result.sqlServerName && (<><div className="muted">Reported server</div><div>{result.sqlServerName}</div></>)}
                {result.sqlProcName && (<><div className="muted">Proc</div><div>{result.sqlProcName}</div></>)}
              </div>
            )}
            <EnvCheckPanel envCheck={result.envCheck} />
            <TokenClaimsPanel claims={result.tokenClaims} tokenError={result.tokenError} />
            <DebugDumpPanel result={result} />
          </>
        )}

        {ok && result.rows && (
          <>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 8 }}>
              <button className="btn" onClick={() => setShowRaw(s => !s)} style={{ fontSize: 12 }}>
                {showRaw ? 'Show table' : 'Show raw JSON'}
              </button>
              <button
                className="btn"
                onClick={() => navigator.clipboard.writeText(JSON.stringify(result.rows, null, 2))}
                style={{ fontSize: 12 }}
              >
                <Copy size={12} /> Copy JSON
              </button>
            </div>
            {showRaw
              ? <RawJson rows={result.rows} />
              : <RowsTable rows={result.rows} />}
          </>
        )}
      </div>
    </section>
  );
}

function ConnectionMeta({ result }: { result: PeDbTestResponse }) {
  const items: [string, string][] = [
    ['Server',   result.server],
    ['Database', result.database],
    ['User',     result.user],
    ['Query',    result.query],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 4, fontSize: 12 }}>
      {items.map(([k, v]) => (
        <React.Fragment key={k}>
          <div className="muted">{k}</div>
          <div style={{ fontFamily: k === 'Query' ? 'Consolas, ui-monospace, monospace' : undefined, wordBreak: 'break-word' }}>
            {v || <span className="muted">—</span>}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function RowsTable({ rows }: { rows: any[] }) {
  if (!rows || rows.length === 0) {
    return <div className="muted" style={{ fontSize: 13 }}>Query returned no rows.</div>;
  }
  // FOR JSON AUTO returns a single-cell row with the JSON payload split into
  // chunks under a column like "JSON_F52E2B61-18A1-11d1-B105-00805F49916B".
  // Render normally — the user can flip to raw to read it.
  const columns = Array.from(
    rows.reduce<Set<string>>((acc, row) => {
      if (row && typeof row === 'object') Object.keys(row).forEach(k => acc.add(k));
      return acc;
    }, new Set<string>())
  );

  return (
    <div style={{ overflowX: 'auto', border: '1px solid #27303b', borderRadius: 8 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead>
          <tr style={{ background: '#11151b' }}>
            {columns.map(c => (
              <th key={c} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #27303b', whiteSpace: 'nowrap' }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #1c232c' }}>
              {columns.map(c => (
                <td key={c} style={{ padding: '6px 10px', verticalAlign: 'top', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatCell(row?.[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// SQL 18456 state codes — the actual reason a login was rejected. Microsoft
// surfaces the number, not the explanation; this maps the common ones.
function SqlNumberHint({ n }: { n?: number }) {
  if (!n) return null;
  const hints: Record<number, string> = {
    18456: '— login failed (see state)',
    40615: '— firewall: client IP not allowed on this server',
    40532: '— cannot open server name requested (routing)',
    4060:  '— cannot open the requested database',
    18452: '— login from an untrusted domain (Windows auth)',
  };
  const text = hints[n];
  return text ? <span className="muted">{text}</span> : null;
}

function SqlStateHint({ number, state }: { number?: number; state?: number }) {
  if (number !== 18456 || state == null) return null;
  const map: Record<number, string> = {
    1:   'password incorrect (state masked — real reason in server log)',
    2:   'login does not exist on this server',
    5:   'login does not exist on this server',
    6:   'attempt to use a Windows login name with SQL auth',
    7:   'login disabled and password mismatch',
    8:   'password mismatch',
    9:   'invalid password',
    11:  'valid login but server access failed (contained DB / firewall)',
    12:  'valid login but server access failed',
    38:  'cannot find database',
    40:  'database does not exist',
    58:  'SQL auth used but server only accepts Windows auth',
  };
  const text = map[state];
  return text ? <span className="muted">— {text}</span> : null;
}

function EnvCheckPanel({ envCheck }: { envCheck?: PeDbTestResponse['envCheck'] }) {
  if (!envCheck) return null;
  const rows: [string, string, boolean][] = [
    ['PE_SQL_SERVER',   envCheck.PE_SQL_SERVER_value || '(missing)',   !!envCheck.PE_SQL_SERVER],
    ['PE_SQL_DATABASE', envCheck.PE_SQL_DATABASE_value || '(missing)', !!envCheck.PE_SQL_DATABASE],
    ['PE_SQL_USER',     envCheck.PE_SQL_USER_value || '(missing)',     !!envCheck.PE_SQL_USER],
    [
      'PE_SQL_PASSWORD',
      envCheck.PE_SQL_PASSWORD_present
        ? `${envCheck.PE_SQL_PASSWORD_preview} (length ${envCheck.PE_SQL_PASSWORD_length})`
        : '(missing or empty)',
      !!envCheck.PE_SQL_PASSWORD_present,
    ],
  ];
  return (
    <div style={{
      marginTop: 14, padding: 12, borderRadius: 8,
      border: '1px solid #27303b', background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Env vars (server-side)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 4, fontSize: 12 }}>
        {rows.map(([k, v, ok]) => (
          <React.Fragment key={k}>
            <div className="muted">{k}</div>
            <div style={{ color: ok ? undefined : '#fca5a5', wordBreak: 'break-word' }}>{v}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function TokenClaimsPanel({
  claims, tokenError,
}: { claims?: PeDbTestResponse['tokenClaims']; tokenError?: string | null }) {
  if (!claims && !tokenError) return null;
  return (
    <div style={{
      marginTop: 14, padding: 12, borderRadius: 8,
      border: '1px solid #27303b', background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>AAD token claims</div>
      {tokenError && (
        <div style={{ color: '#fca5a5', fontSize: 12, marginBottom: 8 }}>
          Token acquisition failed: {tokenError}
        </div>
      )}
      {claims && (
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 4, fontSize: 12 }}>
          {Object.entries(claims).map(([k, v]) => (
            <React.Fragment key={k}>
              <div className="muted">{k}</div>
              <div style={{ wordBreak: 'break-all', fontFamily: 'Consolas, ui-monospace, monospace' }}>
                {v == null ? <span className="muted">—</span> : String(v)}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function DebugDumpPanel({ result }: { result: PeDbTestResponse }) {
  const [open, setOpen] = useState(false);
  if (!result.errorChain && !result.errorInspected) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <button className="btn" onClick={() => setOpen(o => !o)} style={{ fontSize: 12 }}>
        {open ? 'Hide raw error dump' : 'Show raw error dump'}
      </button>
      {open && (
        <>
          {result.errorChain && (
            <pre style={dumpStyle}>{JSON.stringify(result.errorChain, null, 2)}</pre>
          )}
          {result.errorInspected && (
            <pre style={dumpStyle}>{result.errorInspected}</pre>
          )}
        </>
      )}
    </div>
  );
}

const dumpStyle: React.CSSProperties = {
  background: '#0b0d10',
  border: '1px solid #27303b',
  color: '#e9eef5',
  padding: 12, borderRadius: 8, marginTop: 8,
  maxHeight: 360, overflow: 'auto',
  fontSize: 11,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

function RawJson({ rows }: { rows: any[] }) {
  return (
    <pre style={{
      background: '#0b0d10',
      border: '1px solid #27303b',
      color: '#e9eef5',
      padding: 12, borderRadius: 8,
      maxHeight: 480, overflow: 'auto',
      fontSize: 12,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
      {JSON.stringify(rows, null, 2)}
    </pre>
  );
}
